import { isEmpty } from "lodash/fp";
import { DateTime } from "luxon";

import { get } from "@readai/common/api";
import type {
  ApiSearchResults,
  ApiSessionsListEntity,
  CreateSessionParams,
  SearchRequestParams,
  SessionsListQuery,
  UnauthenticatedSession,
} from "@readai/common/types";
import { withQueryParamsUrl } from "@readai/common/utils";

export const getActiveSessions = async (
  meetingInstanceId: CreateSessionParams["meetingInstanceId"],
): Promise<UnauthenticatedSession[]> => {
  const queryParams = {
    meeting_instance_id: meetingInstanceId,
    state: "active",
  };

  return get(withQueryParamsUrl("/public/sessions", queryParams));
};

export const searchSessions = async (
  request: SearchRequestParams,
): Promise<ApiSearchResults> => {
  const params = {
    start_date: request.startDate?.toISO(),
    end_date: request.endDate?.toISO(),
    search: request.query,
    session_id: request.sessionId,
    owner_user_id: request.ownerUserId,
    owner_team_id: request.ownerTeamId,
    owner_workspace_id: request.ownerWorkspaceId,
  };

  let url = withQueryParamsUrl("/sessions/search", params);

  // TODO - handle multiple-value params in the query param helpers
  if (!isEmpty(request.categories)) {
    const categoriesParams = request
      .categories!.map((c) => `category=${c}`)
      .join("&");
    url = `${url}&${categoriesParams}`;
  }

  return get(url, { withCreds: true });
};

const queryParamValue = (value?: string | number | null) =>
  value ? encodeURIComponent(value) : null;

const getDateString = (date?: DateTime | string) => {
  if (!date) return null;
  if (typeof date === "string") return date;
  return date.toISO();
};

export const listSessions = async (
  query: SessionsListQuery,
  isForCurrentUser = true,
): Promise<ApiSessionsListEntity[]> => {
  const url = withQueryParamsUrl("/sessions", {
    include_summary_fields: query.includeSummaryFields,
    owner_user_id: isForCurrentUser ? null : queryParamValue(query.ownerUserId),
    owner_team_id: isForCurrentUser ? null : queryParamValue(query.ownerTeamId),
    owner_workspace_id: queryParamValue(query.ownerWorkspaceId),
    view: queryParamValue(query.view),
    page: queryParamValue(query.page),
    count: queryParamValue(query.count),
    start_date: getDateString(query.startDate),
    end_date: getDateString(query.endDate),
    recurring_event_id: query.recurringEventId,
  });

  return get(url, { withCreds: true });
};
